import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { PageLayout } from "../components/PageLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import {
  Hotel,
  Search,
  Globe,
  Mail,
  Phone,
  Star,
  MapPin,
  ExternalLink,
  Filter,
  Plus,
  Send,
  BarChart3,
  Users,
  ArrowRight,
  Loader2,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Trash2,
  Edit,
  Eye,
  X,
  Check,
  Clock,
  Building2,
  Sparkles,
} from "lucide-react";

const API = `${window.location.origin}/api`;

const PIPELINE_STATUSES = [
  { value: "da_contattare", label: "Da Contattare", color: "bg-gray-500" },
  { value: "email_inviata", label: "Email Inviata", color: "bg-blue-500" },
  { value: "interessato", label: "Interessato", color: "bg-yellow-500" },
  { value: "call_fissata", label: "Call Fissata", color: "bg-purple-500" },
  { value: "partner", label: "Partner", color: "bg-green-500" },
  { value: "non_interessato", label: "Non Interessato", color: "bg-red-500" },
];

const PRIORITY_COLORS = {
  alta: "bg-red-100 text-red-700 border-red-200",
  media: "bg-yellow-100 text-yellow-700 border-yellow-200",
  bassa: "bg-gray-100 text-gray-600 border-gray-200",
};

const TABS = [
  { id: "search", label: "Ricerca Hotel", icon: Search },
  { id: "generic", label: "Ricerca Generica", icon: Globe },
  { id: "hotels", label: "Lista Hotel", icon: Building2 },
  { id: "campaigns", label: "Campagne ODR", icon: Mail },
  { id: "pipeline", label: "Pipeline CRM", icon: Users },
  { id: "stats", label: "Statistiche", icon: BarChart3 },
];

// ==================== TAB: RICERCA GENERICA (Scuole, Farmacie, Palestre, ecc.) ====================
const GenericSearchTab = () => {
  const [categories, setCategories] = useState([]);
  const [regions, setRegions] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("scuola-estetica");
  const [selectedRegion, setSelectedRegion] = useState("");
  const [customKeywords, setCustomKeywords] = useState("");
  const [jobId, setJobId] = useState(null);
  const [status, setStatus] = useState(null);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axios.get(`${API}/hotel-spa/generic-search/categories`).then((res) => {
      setCategories(res.data.categories || []);
      setRegions(res.data.regions || []);
    }).catch(() => {});
  }, []);

  // Polling job status
  useEffect(() => {
    if (!jobId) return;
    const poll = async () => {
      try {
        const res = await axios.get(`${API}/hotel-spa/generic-search/status/${jobId}`);
        setStatus(res.data);
        if (res.data.status === "done") {
          setResults(res.data.results || []);
          setLoading(false);
          toast.success(`Ricerca completata: ${res.data.total_found} risultati`);
        } else if (res.data.status === "error") {
          setLoading(false);
          toast.error("Errore Overpass: " + (res.data.error || ""));
        }
      } catch (e) { /* keep polling */ }
    };
    const interval = setInterval(() => {
      if (status?.status === "done" || status?.status === "error") return;
      poll();
    }, 3000);
    poll();
    return () => clearInterval(interval);
  }, [jobId, status?.status]);

  const startSearch = async () => {
    if (!selectedCategory) return toast.error("Seleziona una categoria");
    setLoading(true);
    setResults([]);
    setStatus({ status: "queued" });
    try {
      const custom = customKeywords.split(",").map((s) => s.trim()).filter(Boolean);
      const res = await axios.post(`${API}/hotel-spa/generic-search`, {
        category: selectedCategory,
        region: selectedRegion || null,
        custom_keywords: custom.length > 0 ? custom : null,
      });
      setJobId(res.data.job_id);
      toast.info(`Ricerca avviata${selectedRegion ? " in " + selectedRegion : " a livello nazionale"}. Attendere 30-120s…`);
    } catch (e) {
      setLoading(false);
      toast.error("Errore avvio: " + (e.response?.data?.detail || e.message));
    }
  };

  const selectedCat = categories.find((c) => c.slug === selectedCategory);
  const exportCSV = () => {
    if (results.length === 0) return;
    const headers = ["name", "city", "region", "address", "postcode", "phone", "email", "website", "lat", "lng"];
    const rows = [headers.join(";"), ...results.map((r) => headers.map((h) => (String(r[h] || "").replace(/"/g, '""'))).map((v) => `"${v}"`).join(";"))];
    const blob = new Blob([rows.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${selectedCategory}-${selectedRegion || "italia"}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6" data-testid="generic-search-tab">
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Globe className="w-5 h-5 text-violet-600" />
            Ricerca generica · Motore aggregato
          </CardTitle>
          <p className="text-xs text-muted-foreground mt-1">
            Cerca attività per categoria e regione. Aggrega risultati da <strong>PagineGialle, Google Maps, siti scuola, Yelp, Cylex</strong> e altre directory italiane. Copertura: 600+ scuole estetica, 40.000+ parrucchieri, ecc.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid md:grid-cols-3 gap-3">
            <div>
              <label className="text-xs font-semibold text-gray-700 mb-1 block">Categoria</label>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full h-9 rounded-md border border-input bg-white px-3 text-sm"
                data-testid="generic-category-select"
              >
                {categories.map((c) => (
                  <option key={c.slug} value={c.slug}>{c.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-700 mb-1 block">Regione</label>
              <select
                value={selectedRegion}
                onChange={(e) => setSelectedRegion(e.target.value)}
                className="w-full h-9 rounded-md border border-input bg-white px-3 text-sm"
                data-testid="generic-region-select"
              >
                <option value="">🇮🇹 Tutta Italia (nazionale)</option>
                {regions.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-700 mb-1 block">Parole chiave extra (opz.)</label>
              <Input
                value={customKeywords}
                onChange={(e) => setCustomKeywords(e.target.value)}
                placeholder="es: academy, IPSIA (separati da virgole)"
                className="h-9"
                data-testid="generic-custom-keywords"
              />
            </div>
          </div>
          {selectedCat && (
            <div className="bg-violet-50 border border-violet-200 rounded-md p-3">
              <p className="text-[11px] font-semibold text-violet-800 uppercase tracking-wider mb-1">Sinonimi cercati automaticamente:</p>
              <p className="text-xs text-violet-700">{selectedCat.synonyms.join(" · ")}</p>
            </div>
          )}
          <div className="bg-emerald-50 border border-emerald-200 rounded-md p-3 text-xs text-emerald-800" data-testid="csv-format-hint">
            <p className="font-semibold mb-1">💡 Import CSV — formato accettato</p>
            <p>Colonne (con intestazione, separatore <b>;</b> o <b>,</b>): <code className="bg-white px-1 rounded">nome; città; indirizzo; telefono; email; website; regione; cap</code></p>
            <p className="mt-1">Solo <b>nome</b> è obbligatorio. Gli altri sono opzionali. Accettati anche nomi inglesi (name, city, phone, ecc.).</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button
              onClick={startSearch}
              disabled={loading}
              className="bg-violet-600 hover:bg-violet-700 text-white"
              data-testid="generic-search-btn"
            >
              {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Ricerca in corso…</> : <><Search className="w-4 h-4 mr-2" /> Cerca</>}
            </Button>
            {results.length > 0 && (
              <Button variant="outline" onClick={exportCSV} data-testid="generic-export-csv-btn">
                📥 Esporta CSV ({results.length})
              </Button>
            )}
            <label className="cursor-pointer inline-flex items-center gap-1 h-9 px-3 rounded-md text-sm font-medium bg-emerald-600 hover:bg-emerald-700 text-white border border-emerald-600" data-testid="generic-import-csv-label" title="Carica un CSV con colonne: nome, città, indirizzo, telefono, email, website">
              ⬆️ Import CSV
              <input
                type="file"
                accept=".csv,text/csv"
                className="hidden"
                data-testid="generic-import-csv-input"
                onChange={async (e) => {
                  const f = e.target.files?.[0];
                  if (!f) return;
                  const fd = new FormData();
                  fd.append("file", f);
                  fd.append("category", selectedCategory);
                  fd.append("region", selectedRegion || "Italia");
                  try {
                    const res = await axios.post(`${API}/hotel-spa/generic-search/import-csv`, fd);
                    toast.success(`Import OK: ${res.data.imported} record ${res.data.errors?.length ? `(${res.data.errors.length} errori)` : ""}`);
                    // Ricarica history
                    const hist = await axios.get(`${API}/hotel-spa/generic-search/history?category=${selectedCategory}${selectedRegion ? `&region=${selectedRegion}` : ""}&limit=1000`);
                    setResults(hist.data.results || []);
                  } catch (err) {
                    toast.error("Errore import: " + (err.response?.data?.detail || err.message));
                  }
                  e.target.value = "";
                }}
              />
            </label>
          </div>
          {status && (
            <div className="text-xs text-gray-600">
              Stato: <span className="font-semibold">{status.status}</span>
              {status.total_found !== undefined && ` · ${status.total_found} risultati`}
              {status.error && <span className="text-red-600"> · {status.error}</span>}
            </div>
          )}
        </CardContent>
      </Card>

      {results.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Risultati · {results.length}</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="max-h-[560px] overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 sticky top-0">
                  <tr className="text-left border-b">
                    <th className="p-2">Nome</th>
                    <th className="p-2">Città</th>
                    <th className="p-2">Telefono</th>
                    <th className="p-2">Email</th>
                    <th className="p-2">Website</th>
                  </tr>
                </thead>
                <tbody>
                  {results.map((r, i) => (
                    <tr key={r.id || i} className="border-b hover:bg-gray-50" data-testid={`generic-result-${i}`}>
                      <td className="p-2 font-semibold text-violet-800">{r.name}</td>
                      <td className="p-2 text-gray-600">{r.city || "—"}</td>
                      <td className="p-2 text-gray-600">{r.phone || "—"}</td>
                      <td className="p-2 text-gray-600">{r.email || "—"}</td>
                      <td className="p-2 text-gray-600">
                        {r.website ? <a href={r.website} target="_blank" rel="noopener noreferrer" className="text-violet-600 hover:underline">Sito ↗</a> : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// ==================== TAB: CONTATTI FIERA ====================
const FairContactsTab = () => {
  const [contacts, setContacts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState("");
  const [filterEvento, setFilterEvento] = useState("");
  const [filterAgente, setFilterAgente] = useState("");
  const [filterStato, setFilterStato] = useState("");
  const [filterNazione, setFilterNazione] = useState("");
  const [editingContact, setEditingContact] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const PER_PAGE = 50;

  const loadContacts = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append("search", search);
      if (filterEvento) params.append("evento", filterEvento);
      if (filterAgente) params.append("agente", filterAgente);
      if (filterStato) params.append("stato", filterStato);
      if (filterNazione) params.append("nazione", filterNazione);
      params.append("skip", page * PER_PAGE);
      params.append("limit", PER_PAGE);

      const [contactsRes, statsRes] = await Promise.all([
        axios.get(`${API}/hotel-spa/fair-contacts?${params}`),
        axios.get(`${API}/hotel-spa/fair-contacts/stats`),
      ]);
      setContacts(contactsRes.data.contacts);
      setTotal(contactsRes.data.total);
      setStats(statsRes.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [search, filterEvento, filterAgente, filterStato, filterNazione, page]);

  useEffect(() => { loadContacts(); }, [loadContacts]);

  const startEdit = (c) => {
    setEditingContact(c.contact_id);
    setEditForm({ ...c });
  };

  const cancelEdit = () => {
    setEditingContact(null);
    setEditForm({});
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/hotel-spa/fair-contacts/${editingContact}`, editForm);
      toast.success("Contatto aggiornato");
      setEditingContact(null);
      loadContacts();
    } catch (e) {
      toast.error("Errore aggiornamento");
    } finally {
      setSaving(false);
    }
  };

  const deleteContact = async (id) => {
    if (!window.confirm("Eliminare questo contatto?")) return;
    try {
      await axios.delete(`${API}/hotel-spa/fair-contacts/${id}`);
      toast.success("Contatto eliminato");
      loadContacts();
    } catch (e) {
      toast.error("Errore eliminazione");
    }
  };

  const addContact = async () => {
    if (!editForm.nome_azienda) { toast.error("Nome azienda richiesto"); return; }
    try {
      await axios.post(`${API}/hotel-spa/fair-contacts`, editForm);
      toast.success("Contatto aggiunto");
      setShowAdd(false);
      setEditForm({});
      loadContacts();
    } catch (e) {
      toast.error("Errore creazione");
    }
  };

  const statoColor = (s) => {
    if (s === "TRATTATIVA CHIUSA") return "bg-green-100 text-green-700";
    if (s === "TRATTATIVA APERTA") return "bg-yellow-100 text-yellow-700";
    if (s === "IN ATTESA DI FEEDBACK") return "bg-blue-100 text-blue-700";
    if (s === "IN ATTESA DI SAMPLE") return "bg-purple-100 text-purple-700";
    return "bg-gray-100 text-gray-600";
  };

  return (
    <div className="space-y-4">
      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="fair-stats">
          <Card className="rounded-xl border-violet-200/50 bg-violet-50/30">
            <CardContent className="p-3 text-center">
              <p className="text-2xl font-bold text-violet-700">{stats.total}</p>
              <p className="text-xs text-muted-foreground">Totale Contatti</p>
            </CardContent>
          </Card>
          <Card className="rounded-xl border-green-200/50 bg-green-50/30">
            <CardContent className="p-3 text-center">
              <p className="text-2xl font-bold text-green-700">{stats.with_email}</p>
              <p className="text-xs text-muted-foreground">Con Email</p>
            </CardContent>
          </Card>
          {Object.entries(stats.by_agente).slice(0, 3).map(([ag, count]) => (
            <Card key={ag} className="rounded-xl border-border/50">
              <CardContent className="p-3 text-center">
                <p className="text-2xl font-bold">{count}</p>
                <p className="text-xs text-muted-foreground">{ag}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Filters */}
      <Card className="rounded-xl border-border/50">
        <CardContent className="p-3">
          <div className="flex flex-wrap gap-2 items-center">
            <Input
              placeholder="Cerca azienda, persona, email..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(0); }}
              className="max-w-[220px] rounded-full text-sm h-9"
              data-testid="fair-search"
            />
            <select
              value={filterEvento}
              onChange={(e) => { setFilterEvento(e.target.value); setPage(0); }}
              className="h-9 rounded-full border px-3 text-sm bg-white"
              data-testid="fair-filter-evento"
            >
              <option value="">Tutti Eventi</option>
              {EVENTI.map(e => <option key={e} value={e}>{e}</option>)}
            </select>
            <select
              value={filterAgente}
              onChange={(e) => { setFilterAgente(e.target.value); setPage(0); }}
              className="h-9 rounded-full border px-3 text-sm bg-white"
              data-testid="fair-filter-agente"
            >
              <option value="">Tutti Agenti</option>
              {AGENTI.map(a => <option key={a} value={a}>{a}</option>)}
              <option value="NON_ASSEGNATO">Non Assegnato</option>
            </select>
            <select
              value={filterStato}
              onChange={(e) => { setFilterStato(e.target.value); setPage(0); }}
              className="h-9 rounded-full border px-3 text-sm bg-white"
              data-testid="fair-filter-stato"
            >
              <option value="">Tutti Stati</option>
              {FAIR_STATI.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <Input
              placeholder="Nazione..."
              value={filterNazione}
              onChange={(e) => { setFilterNazione(e.target.value); setPage(0); }}
              className="max-w-[120px] rounded-full text-sm h-9"
            />
            <div className="ml-auto flex gap-2">
              <Button size="sm" variant="outline" className="rounded-full" onClick={() => { setShowAdd(true); setEditForm({ evento: "COSMOPROF BOLOGNA 2026" }); }} data-testid="add-fair-contact-btn">
                <Plus className="w-4 h-4 mr-1" /> Aggiungi
              </Button>
              <Button size="sm" variant="outline" className="rounded-full" onClick={loadContacts}>
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Add Contact Form */}
      {showAdd && (
        <Card className="rounded-xl border-green-200 bg-green-50/30" data-testid="add-contact-form">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Nuovo Contatto</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <Input placeholder="Nome azienda *" value={editForm.nome_azienda || ""} onChange={(e) => setEditForm({ ...editForm, nome_azienda: e.target.value })} className="text-sm" />
              <Input placeholder="Persona riferimento" value={editForm.persona_riferimento || ""} onChange={(e) => setEditForm({ ...editForm, persona_riferimento: e.target.value })} className="text-sm" />
              <Input placeholder="Email" value={editForm.email || ""} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} className="text-sm" />
              <Input placeholder="Telefono" value={editForm.telefono || ""} onChange={(e) => setEditForm({ ...editForm, telefono: e.target.value })} className="text-sm" />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <Input placeholder="Nazione" value={editForm.nazione || ""} onChange={(e) => setEditForm({ ...editForm, nazione: e.target.value })} className="text-sm" />
              <Input placeholder="Regione" value={editForm.regione || ""} onChange={(e) => setEditForm({ ...editForm, regione: e.target.value })} className="text-sm" />
              <select value={editForm.in_carico_a || ""} onChange={(e) => setEditForm({ ...editForm, in_carico_a: e.target.value })} className="h-9 border rounded px-2 text-sm bg-white">
                <option value="">Agente...</option>
                {AGENTI.map(a => <option key={a} value={a}>{a}</option>)}
              </select>
              <select value={editForm.tipo || ""} onChange={(e) => setEditForm({ ...editForm, tipo: e.target.value })} className="h-9 border rounded px-2 text-sm bg-white">
                <option value="">Tipo...</option>
                <option value="Distributore">Distributore</option>
                <option value="Centro Estetico">Centro Estetico</option>
                <option value="Distribuzione internazionale">Distribuzione internazionale</option>
                <option value="Altro">Altro</option>
              </select>
            </div>
            <Input placeholder="Note" value={editForm.note || ""} onChange={(e) => setEditForm({ ...editForm, note: e.target.value })} className="text-sm" />
            <div className="flex gap-2">
              <Button size="sm" onClick={addContact} className="rounded-full bg-green-600 hover:bg-green-700"><Check className="w-4 h-4 mr-1" /> Salva</Button>
              <Button size="sm" variant="outline" className="rounded-full" onClick={() => { setShowAdd(false); setEditForm({}); }}>Annulla</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Contacts Table */}
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-violet-500" /></div>
      ) : (
        <>
          <p className="text-xs text-muted-foreground">{total} contatti trovati &middot; Pagina {page + 1} di {Math.ceil(total / PER_PAGE)}</p>

          <div className="overflow-x-auto rounded-xl border">
            <table className="w-full text-sm" data-testid="fair-contacts-table">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="p-2 text-left font-medium text-xs">Evento</th>
                  <th className="p-2 text-left font-medium text-xs">Azienda</th>
                  <th className="p-2 text-left font-medium text-xs">Tipo</th>
                  <th className="p-2 text-left font-medium text-xs">Nazione</th>
                  <th className="p-2 text-left font-medium text-xs">Regione</th>
                  <th className="p-2 text-left font-medium text-xs">Riferimento</th>
                  <th className="p-2 text-left font-medium text-xs">Email</th>
                  <th className="p-2 text-left font-medium text-xs">Telefono</th>
                  <th className="p-2 text-left font-medium text-xs">Agente</th>
                  <th className="p-2 text-left font-medium text-xs">Stato</th>
                  <th className="p-2 text-left font-medium text-xs">Note</th>
                  <th className="p-2 text-center font-medium text-xs w-[90px]">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {contacts.map((c) => (
                  <tr key={c.contact_id} className="border-b hover:bg-gray-50/50 transition-colors" data-testid={`fair-row-${c.contact_id}`}>
                    {editingContact === c.contact_id ? (
                      <>
                        <td className="p-1">
                          <select value={editForm.evento || ""} onChange={(e) => setEditForm({ ...editForm, evento: e.target.value })} className="w-full text-xs border rounded p-1 bg-white">
                            {EVENTI.map(ev => <option key={ev} value={ev}>{ev}</option>)}
                          </select>
                        </td>
                        <td className="p-1"><Input value={editForm.nome_azienda || ""} onChange={(e) => setEditForm({ ...editForm, nome_azienda: e.target.value })} className="text-xs h-7" /></td>
                        <td className="p-1">
                          <select value={editForm.tipo || ""} onChange={(e) => setEditForm({ ...editForm, tipo: e.target.value })} className="w-full text-xs border rounded p-1 bg-white">
                            <option value="">-</option>
                            <option value="Distributore">Distributore</option>
                            <option value="Centro Estetico">Centro Estetico</option>
                            <option value="Distribuzione internazionale">Distr. Internaz.</option>
                            <option value="Altro">Altro</option>
                          </select>
                        </td>
                        <td className="p-1"><Input value={editForm.nazione || ""} onChange={(e) => setEditForm({ ...editForm, nazione: e.target.value })} className="text-xs h-7" /></td>
                        <td className="p-1"><Input value={editForm.regione || ""} onChange={(e) => setEditForm({ ...editForm, regione: e.target.value })} className="text-xs h-7" /></td>
                        <td className="p-1"><Input value={editForm.persona_riferimento || ""} onChange={(e) => setEditForm({ ...editForm, persona_riferimento: e.target.value })} className="text-xs h-7" /></td>
                        <td className="p-1"><Input value={editForm.email || ""} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} className="text-xs h-7" /></td>
                        <td className="p-1"><Input value={editForm.telefono || ""} onChange={(e) => setEditForm({ ...editForm, telefono: e.target.value })} className="text-xs h-7" /></td>
                        <td className="p-1">
                          <select value={editForm.in_carico_a || ""} onChange={(e) => setEditForm({ ...editForm, in_carico_a: e.target.value })} className="w-full text-xs border rounded p-1 bg-white">
                            <option value="">-</option>
                            {AGENTI.map(a => <option key={a} value={a}>{a}</option>)}
                          </select>
                        </td>
                        <td className="p-1">
                          <select value={editForm.stato || ""} onChange={(e) => setEditForm({ ...editForm, stato: e.target.value })} className="w-full text-xs border rounded p-1 bg-white">
                            {FAIR_STATI.map(s => <option key={s} value={s}>{s}</option>)}
                          </select>
                        </td>
                        <td className="p-1"><Input value={editForm.note || ""} onChange={(e) => setEditForm({ ...editForm, note: e.target.value })} className="text-xs h-7" /></td>
                        <td className="p-1 text-center">
                          <div className="flex gap-1 justify-center">
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-green-600" onClick={saveEdit} disabled={saving} data-testid="save-edit-btn">
                              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                            </Button>
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-gray-400" onClick={cancelEdit}><X className="w-3 h-3" /></Button>
                          </div>
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="p-2 text-xs whitespace-nowrap">{c.evento?.replace("COSMOPROF BOLOGNA ", "CP ") || "-"}</td>
                        <td className="p-2 text-xs font-medium max-w-[140px] truncate">{c.nome_azienda || "-"}</td>
                        <td className="p-2 text-xs">{c.tipo || "-"}</td>
                        <td className="p-2 text-xs">{c.nazione || "-"}</td>
                        <td className="p-2 text-xs">{c.regione || "-"}</td>
                        <td className="p-2 text-xs">{c.persona_riferimento || "-"}</td>
                        <td className="p-2 text-xs max-w-[160px] truncate">{c.email ? <a href={`mailto:${c.email}`} className="text-violet-600 hover:underline">{c.email}</a> : "-"}</td>
                        <td className="p-2 text-xs">{c.telefono || "-"}</td>
                        <td className="p-2 text-xs font-medium">{c.in_carico_a || <span className="text-red-400">-</span>}</td>
                        <td className="p-2"><Badge variant="outline" className={`text-[10px] ${statoColor(c.stato)}`}>{c.stato}</Badge></td>
                        <td className="p-2 text-xs max-w-[120px] truncate text-muted-foreground">{c.note || "-"}</td>
                        <td className="p-2 text-center">
                          <div className="flex gap-0.5 justify-center">
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-violet-600" onClick={() => startEdit(c)} data-testid={`edit-${c.contact_id}`}><Edit className="w-3 h-3" /></Button>
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-400 hover:text-red-600" onClick={() => deleteContact(c.contact_id)}><Trash2 className="w-3 h-3" /></Button>
                          </div>
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {total > PER_PAGE && (
            <div className="flex justify-center gap-2 pt-2">
              <Button size="sm" variant="outline" className="rounded-full" disabled={page === 0} onClick={() => setPage(p => p - 1)}>
                Precedente
              </Button>
              <span className="text-sm text-muted-foreground self-center">
                {page + 1} / {Math.ceil(total / PER_PAGE)}
              </span>
              <Button size="sm" variant="outline" className="rounded-full" disabled={(page + 1) * PER_PAGE >= total} onClick={() => setPage(p => p + 1)}>
                Successiva
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
};


// ==================== TAB: RICERCA HOTEL ====================
const SearchTab = () => {
  const [country, setCountry] = useState("Italia");
  const [city, setCity] = useState("");
  const [minStars, setMinStars] = useState(4);
  const [searching, setSearching] = useState(false);
  const [searches, setSearches] = useState([]);
  const [polling, setPolling] = useState(null);

  const loadSearches = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/hotel-spa/searches`);
      setSearches(res.data);
    } catch (e) {
      console.error(e);
    }
  }, []);

  useEffect(() => {
    loadSearches();
  }, [loadSearches]);

  useEffect(() => {
    return () => {
      if (polling) clearInterval(polling);
    };
  }, [polling]);

  const startSearch = async () => {
    if (!country) {
      toast.error("Inserisci almeno il paese");
      return;
    }
    setSearching(true);
    try {
      const res = await axios.post(`${API}/hotel-spa/search`, {
        country,
        city: city || null,
        min_stars: minStars,
      });
      toast.success(`Ricerca avviata: ${res.data.search_id}`);

      const interval = setInterval(async () => {
        try {
          const status = await axios.get(`${API}/hotel-spa/search/${res.data.search_id}`);
          if (status.data.status !== "in_progress") {
            clearInterval(interval);
            setPolling(null);
            setSearching(false);
            loadSearches();
            toast.success(`Ricerca completata: ${status.data.results_count} hotel trovati`);
          }
        } catch (err) {
          clearInterval(interval);
          setPolling(null);
          setSearching(false);
        }
      }, 3000);
      setPolling(interval);
    } catch (e) {
      toast.error("Errore nella ricerca");
      setSearching(false);
    }
  };

  const presetSearches = [
    { country: "Italia", city: "Milano", label: "Milano" },
    { country: "Italia", city: "Roma", label: "Roma" },
    { country: "Italia", city: "Firenze", label: "Firenze" },
    { country: "Italia", city: "Venezia", label: "Venezia" },
    { country: "Schweiz/Suisse/Svizzera/Svizra", city: "", label: "Svizzera" },
    { country: "Deutschland", city: "", label: "Germania" },
    { country: "Dubai", city: "Dubai", label: "Dubai" },
  ];

  return (
    <div className="space-y-6">
      <Card className="rounded-2xl border-border/50">
        <CardHeader>
          <CardTitle className="font-serif text-xl flex items-center gap-2">
            <Search className="w-5 h-5 text-violet-500" />
            Auto Research Claw
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            Cerca hotel 4/5 stelle con SPA e Beauty Center via OpenStreetMap
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="text-sm font-medium mb-1 block">Paese</label>
              <Input
                data-testid="search-country"
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                placeholder="Es: Italia, Deutschland, Dubai"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Citta (opzionale)</label>
              <Input
                data-testid="search-city"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                placeholder="Es: Milano, Roma, Firenze"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Stelle minime</label>
              <select
                data-testid="search-min-stars"
                className="w-full border rounded-lg px-3 py-2 text-sm bg-background"
                value={minStars}
                onChange={(e) => setMinStars(parseInt(e.target.value))}
              >
                <option value={3}>3+ Stelle</option>
                <option value={4}>4+ Stelle</option>
                <option value={5}>Solo 5 Stelle</option>
              </select>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            {presetSearches.map((p) => (
              <Button
                key={p.label}
                variant="outline"
                size="sm"
                className="rounded-full text-xs"
                onClick={() => {
                  setCountry(p.country);
                  setCity(p.city);
                }}
              >
                <MapPin className="w-3 h-3 mr-1" />
                {p.label}
              </Button>
            ))}
          </div>

          <Button
            data-testid="start-search-btn"
            onClick={startSearch}
            disabled={searching}
            className="rounded-full bg-violet-600 hover:bg-violet-700"
          >
            {searching ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Search className="w-4 h-4 mr-2" />
            )}
            {searching ? "Ricerca in corso..." : "Avvia Ricerca Claw"}
          </Button>
        </CardContent>
      </Card>

      {/* Search History */}
      {searches.length > 0 && (
        <Card className="rounded-2xl border-border/50">
          <CardHeader>
            <CardTitle className="font-serif text-lg">Ricerche Recenti</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {searches.map((s) => (
                <div
                  key={s.search_id}
                  className="flex items-center justify-between p-3 bg-secondary/30 rounded-xl"
                >
                  <div className="flex items-center gap-3">
                    <Globe className="w-4 h-4 text-muted-foreground" />
                    <div>
                      <p className="font-medium text-sm">
                        {s.city ? `${s.city}, ` : ""}{s.country}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(s.created_at).toLocaleString("it-IT")}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge
                      variant="outline"
                      className={`text-xs ${
                        s.status === "completed"
                          ? "border-green-300 text-green-700"
                          : s.status === "in_progress"
                          ? "border-blue-300 text-blue-700"
                          : "border-red-300 text-red-700"
                      }`}
                    >
                      {s.status === "completed"
                        ? `${s.results_count} hotel`
                        : s.status === "in_progress"
                        ? "In corso..."
                        : "Errore"}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// ==================== TAB: LISTA HOTEL ====================
const HotelsTab = () => {
  const [hotels, setHotels] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    country: "",
    city: "",
    priority: "",
    pipeline_status: "",
    min_stars: "",
  });
  const [showFilters, setShowFilters] = useState(false);
  const [editingHotel, setEditingHotel] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newHotel, setNewHotel] = useState({
    name: "", city: "", country: "Italia", stars: 4,
    has_spa: true, contact_email: "", contact_phone: "", website: "", notes: "",
  });

  const loadHotels = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.country) params.country = filters.country;
      if (filters.city) params.city = filters.city;
      if (filters.priority) params.priority = filters.priority;
      if (filters.pipeline_status) params.pipeline_status = filters.pipeline_status;
      if (filters.min_stars) params.min_stars = parseInt(filters.min_stars);

      const res = await axios.get(`${API}/hotel-spa/hotels`, { params });
      setHotels(res.data.hotels);
      setTotal(res.data.total);
    } catch (e) {
      toast.error("Errore caricamento hotel");
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadHotels();
  }, [loadHotels]);

  const updatePipeline = async (hotelId, status) => {
    try {
      await axios.put(`${API}/hotel-spa/pipeline/${hotelId}`, {
        hotel_id: hotelId,
        status,
      });
      toast.success(`Stato aggiornato a "${status}"`);
      loadHotels();
    } catch (e) {
      toast.error("Errore aggiornamento");
    }
  };

  const deleteHotel = async (hotelId) => {
    if (!window.confirm("Sei sicuro di voler eliminare questo hotel?")) return;
    try {
      await axios.delete(`${API}/hotel-spa/hotels/${hotelId}`);
      toast.success("Hotel eliminato");
      loadHotels();
    } catch (e) {
      toast.error("Errore eliminazione");
    }
  };

  const addManualHotel = async () => {
    if (!newHotel.name) {
      toast.error("Inserisci almeno il nome dell'hotel");
      return;
    }
    try {
      await axios.post(`${API}/hotel-spa/hotels/manual`, newHotel);
      toast.success("Hotel aggiunto!");
      setShowAddModal(false);
      setNewHotel({
        name: "", city: "", country: "Italia", stars: 4,
        has_spa: true, contact_email: "", contact_phone: "", website: "", notes: "",
      });
      loadHotels();
    } catch (e) {
      toast.error("Errore aggiunta hotel");
    }
  };

  const updateHotel = async (hotelId, data) => {
    try {
      await axios.put(`${API}/hotel-spa/hotels/${hotelId}`, data);
      toast.success("Hotel aggiornato");
      setEditingHotel(null);
      loadHotels();
    } catch (e) {
      toast.error("Errore aggiornamento");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{total} hotel nel database</p>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            className="rounded-full"
            onClick={() => setShowFilters(!showFilters)}
            data-testid="toggle-filters-btn"
          >
            <Filter className="w-4 h-4 mr-1" />
            Filtri
            {showFilters ? <ChevronUp className="w-3 h-3 ml-1" /> : <ChevronDown className="w-3 h-3 ml-1" />}
          </Button>
          <Button
            size="sm"
            className="rounded-full bg-violet-600 hover:bg-violet-700"
            onClick={() => setShowAddModal(true)}
            data-testid="add-hotel-btn"
          >
            <Plus className="w-4 h-4 mr-1" />
            Aggiungi Hotel
          </Button>
        </div>
      </div>

      {showFilters && (
        <Card className="rounded-xl border-border/50">
          <CardContent className="p-4">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <Input
                placeholder="Paese"
                value={filters.country}
                onChange={(e) => setFilters({ ...filters, country: e.target.value })}
                className="text-sm"
              />
              <Input
                placeholder="Citta"
                value={filters.city}
                onChange={(e) => setFilters({ ...filters, city: e.target.value })}
                className="text-sm"
              />
              <select
                className="border rounded-lg px-3 py-2 text-sm bg-background"
                value={filters.priority}
                onChange={(e) => setFilters({ ...filters, priority: e.target.value })}
              >
                <option value="">Tutte le priorita</option>
                <option value="alta">Alta</option>
                <option value="media">Media</option>
                <option value="bassa">Bassa</option>
              </select>
              <select
                className="border rounded-lg px-3 py-2 text-sm bg-background"
                value={filters.pipeline_status}
                onChange={(e) => setFilters({ ...filters, pipeline_status: e.target.value })}
              >
                <option value="">Tutti gli stati</option>
                {PIPELINE_STATUSES.map((s) => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
              <select
                className="border rounded-lg px-3 py-2 text-sm bg-background"
                value={filters.min_stars}
                onChange={(e) => setFilters({ ...filters, min_stars: e.target.value })}
              >
                <option value="">Tutte le stelle</option>
                <option value="3">3+ Stelle</option>
                <option value="4">4+ Stelle</option>
                <option value="5">5 Stelle</option>
              </select>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Add Hotel Modal */}
      {showAddModal && (
        <Card className="rounded-xl border-violet-200 bg-violet-50/30">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-base font-medium">Aggiungi Hotel Manualmente</CardTitle>
            <Button variant="ghost" size="sm" onClick={() => setShowAddModal(false)}>
              <X className="w-4 h-4" />
            </Button>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Input placeholder="Nome Hotel *" value={newHotel.name} onChange={(e) => setNewHotel({ ...newHotel, name: e.target.value })} data-testid="new-hotel-name" />
              <Input placeholder="Citta" value={newHotel.city} onChange={(e) => setNewHotel({ ...newHotel, city: e.target.value })} />
              <Input placeholder="Paese" value={newHotel.country} onChange={(e) => setNewHotel({ ...newHotel, country: e.target.value })} />
              <Input placeholder="Email contatto" value={newHotel.contact_email} onChange={(e) => setNewHotel({ ...newHotel, contact_email: e.target.value })} />
              <Input placeholder="Telefono" value={newHotel.contact_phone} onChange={(e) => setNewHotel({ ...newHotel, contact_phone: e.target.value })} />
              <Input placeholder="Sito web" value={newHotel.website} onChange={(e) => setNewHotel({ ...newHotel, website: e.target.value })} />
            </div>
            <div className="flex gap-3">
              <select
                className="border rounded-lg px-3 py-2 text-sm bg-background"
                value={newHotel.stars}
                onChange={(e) => setNewHotel({ ...newHotel, stars: parseInt(e.target.value) })}
              >
                <option value={3}>3 Stelle</option>
                <option value={4}>4 Stelle</option>
                <option value={5}>5 Stelle</option>
              </select>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={newHotel.has_spa} onChange={(e) => setNewHotel({ ...newHotel, has_spa: e.target.checked })} />
                SPA
              </label>
            </div>
            <Textarea placeholder="Note" value={newHotel.notes} onChange={(e) => setNewHotel({ ...newHotel, notes: e.target.value })} rows={2} />
            <Button onClick={addManualHotel} className="rounded-full bg-violet-600 hover:bg-violet-700" data-testid="save-new-hotel-btn">
              <Plus className="w-4 h-4 mr-1" /> Salva Hotel
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Hotels List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-violet-500" />
        </div>
      ) : hotels.length === 0 ? (
        <Card className="rounded-2xl border-border/50">
          <CardContent className="py-12 text-center">
            <Building2 className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
            <p className="text-muted-foreground">Nessun hotel trovato. Avvia una ricerca o aggiungi manualmente.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {hotels.map((hotel) => (
            <Card key={hotel.hotel_id} className="rounded-xl border-border/50 hover:shadow-md transition-shadow" data-testid={`hotel-card-${hotel.hotel_id}`}>
              <CardContent className="p-4">
                <div className="flex flex-col md:flex-row md:items-center gap-3 justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-sm truncate">{hotel.name}</h3>
                      {hotel.stars > 0 && (
                        <span className="flex items-center text-yellow-500 text-xs">
                          {Array.from({ length: hotel.stars }).map((_, i) => (
                            <Star key={i} className="w-3 h-3 fill-current" />
                          ))}
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                      {hotel.city && (
                        <span className="flex items-center gap-1">
                          <MapPin className="w-3 h-3" /> {hotel.city}
                        </span>
                      )}
                      {hotel.country && <span>{hotel.country}</span>}
                      {hotel.has_spa && <Badge variant="outline" className="text-xs py-0 border-teal-300 text-teal-700">SPA</Badge>}
                      {hotel.has_beauty && <Badge variant="outline" className="text-xs py-0 border-pink-300 text-pink-700">Beauty</Badge>}
                    </div>
                    <div className="flex flex-wrap items-center gap-2 mt-1 text-xs">
                      {hotel.contact_email && (
                        <span className="flex items-center gap-1 text-blue-600">
                          <Mail className="w-3 h-3" /> {hotel.contact_email}
                        </span>
                      )}
                      {hotel.website && (
                        <a href={hotel.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-violet-600 hover:underline">
                          <ExternalLink className="w-3 h-3" /> Sito web
                        </a>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <Badge className={`text-xs ${PRIORITY_COLORS[hotel.priority] || PRIORITY_COLORS.bassa}`}>
                      {hotel.priority}
                    </Badge>
                    <select
                      className="border rounded-lg px-2 py-1 text-xs bg-background"
                      value={hotel.pipeline_status}
                      onChange={(e) => updatePipeline(hotel.hotel_id, e.target.value)}
                    >
                      {PIPELINE_STATUSES.map((s) => (
                        <option key={s.value} value={s.value}>{s.label}</option>
                      ))}
                    </select>
                    <Button variant="ghost" size="sm" onClick={() => deleteHotel(hotel.hotel_id)}>
                      <Trash2 className="w-3 h-3 text-red-500" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

// ==================== TAB: CAMPAGNE ODR ====================
const CampaignsTab = () => {
  const [queues, setQueues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showHotelCampaign, setShowHotelCampaign] = useState(false);
  const [sending, setSending] = useState({});
  const [sendStatus, setSendStatus] = useState(null);
  const [lastBatchResult, setLastBatchResult] = useState(null);
  const [newQueue, setNewQueue] = useState({
    campaign_name: "",
    subject: "",
    emails_text: "",
    html_content: "",
  });
  const [hotelCampaign, setHotelCampaign] = useState({
    campaign_name: "ODR Hotel Partnership IT",
    subject: "ODR - Proposta Partnership Hotel & SPA",
    html_template_url: `${window.location.origin}/email_odr_hotel_final_it.html`,
    target_countries: "Italia",
    min_stars: 0,
    max_batch_per_day: 90,
  });

  const loadQueues = useCallback(async () => {
    try {
      const [queuesRes, statusRes] = await Promise.all([
        axios.get(`${API}/hotel-spa/email-queues`),
        axios.get(`${API}/hotel-spa/send-status`),
      ]);
      setQueues(queuesRes.data);
      setSendStatus(statusRes.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadQueues();
  }, [loadQueues]);

  const createQueue = async () => {
    if (!newQueue.campaign_name || !newQueue.subject || !newQueue.emails_text) {
      toast.error("Compila tutti i campi obbligatori");
      return;
    }

    const emails = newQueue.emails_text
      .split(/[\s,;]+/)
      .map((e) => e.trim().toLowerCase())
      .filter((e) => e.includes("@"));

    if (emails.length === 0) {
      toast.error("Nessuna email valida trovata");
      return;
    }

    try {
      const res = await axios.post(`${API}/hotel-spa/email-queue`, {
        campaign_name: newQueue.campaign_name,
        subject: newQueue.subject,
        html_content: newQueue.html_content || `<p>${newQueue.campaign_name}</p>`,
        emails,
        batch_size: 90,
      });
      toast.success(`${res.data.total_emails} email in coda!`);
      setShowCreate(false);
      setNewQueue({ campaign_name: "", subject: "", emails_text: "", html_content: "" });
      loadQueues();
    } catch (e) {
      toast.error("Errore creazione coda");
    }
  };

  const createHotelCampaign = async () => {
    if (!hotelCampaign.campaign_name || !hotelCampaign.subject) {
      toast.error("Compila nome campagna e oggetto");
      return;
    }
    try {
      const countries = hotelCampaign.target_countries
        .split(",")
        .map((c) => c.trim())
        .filter(Boolean);
      const res = await axios.post(`${API}/hotel-spa/campaign-from-hotels`, {
        campaign_name: hotelCampaign.campaign_name,
        subject: hotelCampaign.subject,
        html_template_url: hotelCampaign.html_template_url,
        target_countries: countries.length > 0 ? countries : null,
        min_stars: hotelCampaign.min_stars,
        max_batch_per_day: hotelCampaign.max_batch_per_day,
      });
      toast.success(`${res.data.total_emails} email in coda da ${res.data.hotels_matched} hotel! Tempo stimato: ${res.data.estimated_days} giorni`);
      setShowHotelCampaign(false);
      loadQueues();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore creazione campagna");
    }
  };

  const sendBatch = async (queueGroup) => {
    setSending((prev) => ({ ...prev, [queueGroup]: true }));
    setLastBatchResult(null);
    try {
      const res = await axios.post(`${API}/hotel-spa/send-batch/${queueGroup}?batch_size=90`);
      setLastBatchResult(res.data);
      if (res.data.quota_exhausted) {
        toast.error(`Quota giornaliera esaurita! Inviate: ${res.data.sent}. Riprova domani.`);
      } else {
        toast.success(res.data.message);
      }
      loadQueues();
    } catch (e) {
      toast.error("Errore invio batch");
    } finally {
      setSending((prev) => ({ ...prev, [queueGroup]: false }));
    }
  };

  const deleteQueue = async (queueGroup) => {
    if (!window.confirm("Eliminare questa coda? L'azione non e' reversibile.")) return;
    try {
      const res = await axios.delete(`${API}/hotel-spa/email-queue/${queueGroup}`);
      toast.success(res.data.message);
      loadQueues();
    } catch (e) {
      toast.error("Errore eliminazione coda");
    }
  };

  return (
    <div className="space-y-4">
      {/* Daily Quota Status */}
      {sendStatus && (
        <Card className="rounded-xl border-violet-200/50 bg-gradient-to-r from-violet-50/40 to-white" data-testid="daily-quota-card">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-violet-800">Quota Giornaliera Resend</p>
                <p className="text-xs text-muted-foreground">{sendStatus.today} &middot; Rate limit: {sendStatus.rate_limit_per_second}/sec, {sendStatus.delay_between_ms}ms delay</p>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-center">
                  <p className="text-2xl font-bold text-violet-700">{sendStatus.sent_today}</p>
                  <p className="text-xs text-muted-foreground">Inviate oggi</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-green-600">{sendStatus.remaining_today}</p>
                  <p className="text-xs text-muted-foreground">Rimanenti</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-gray-400">{sendStatus.daily_limit}</p>
                  <p className="text-xs text-muted-foreground">Limite</p>
                </div>
              </div>
            </div>
            <div className="mt-2 w-full bg-gray-200 rounded-full h-1.5">
              <div
                className={`rounded-full h-1.5 transition-all ${sendStatus.remaining_today === 0 ? "bg-red-500" : "bg-violet-500"}`}
                style={{ width: `${(sendStatus.sent_today / sendStatus.daily_limit) * 100}%` }}
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Last Batch Result */}
      {lastBatchResult && (
        <Card className={`rounded-xl border ${lastBatchResult.quota_exhausted ? "border-red-200 bg-red-50/30" : "border-green-200 bg-green-50/30"}`} data-testid="last-batch-result">
          <CardContent className="p-3 text-sm">
            <p className="font-medium">{lastBatchResult.message}</p>
            <p className="text-xs text-muted-foreground mt-1">
              Quota oggi: {lastBatchResult.daily_sent_today}/{lastBatchResult.daily_limit} &middot; Rimanenti oggi: {lastBatchResult.daily_remaining}
            </p>
          </CardContent>
        </Card>
      )}

      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-sm text-muted-foreground">Gestione code email e campagne</p>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" size="sm" className="rounded-full" onClick={loadQueues}>
            <RefreshCw className="w-4 h-4 mr-1" /> Aggiorna
          </Button>
          <Button
            size="sm"
            className="rounded-full bg-violet-600 hover:bg-violet-700"
            onClick={() => { setShowHotelCampaign(!showHotelCampaign); setShowCreate(false); }}
            data-testid="create-hotel-campaign-btn"
          >
            <Hotel className="w-4 h-4 mr-1" /> Campagna da Hotel DB
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="rounded-full"
            onClick={() => { setShowCreate(!showCreate); setShowHotelCampaign(false); }}
            data-testid="create-queue-btn"
          >
            <Plus className="w-4 h-4 mr-1" /> Coda Manuale
          </Button>
        </div>
      </div>

      {/* Create Hotel Campaign Form */}
      {showHotelCampaign && (
        <Card className="rounded-xl border-violet-200 bg-violet-50/30" data-testid="hotel-campaign-form">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-medium">Crea Campagna da Hotel Database</CardTitle>
            <p className="text-xs text-muted-foreground">Seleziona hotel dal DB con email, crea coda automatica con template IT</p>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Input
                placeholder="Nome campagna *"
                value={hotelCampaign.campaign_name}
                onChange={(e) => setHotelCampaign({ ...hotelCampaign, campaign_name: e.target.value })}
                data-testid="hotel-campaign-name"
              />
              <Input
                placeholder="Oggetto email *"
                value={hotelCampaign.subject}
                onChange={(e) => setHotelCampaign({ ...hotelCampaign, subject: e.target.value })}
                data-testid="hotel-campaign-subject"
              />
            </div>
            <Input
              placeholder="URL template HTML"
              value={hotelCampaign.html_template_url}
              onChange={(e) => setHotelCampaign({ ...hotelCampaign, html_template_url: e.target.value })}
              data-testid="hotel-campaign-template"
            />
            <div className="grid grid-cols-3 gap-3">
              <Input
                placeholder="Paesi (es: Italia, Francia)"
                value={hotelCampaign.target_countries}
                onChange={(e) => setHotelCampaign({ ...hotelCampaign, target_countries: e.target.value })}
                data-testid="hotel-campaign-countries"
              />
              <Input
                placeholder="Stelle minime"
                type="number"
                min="0"
                max="5"
                value={hotelCampaign.min_stars}
                onChange={(e) => setHotelCampaign({ ...hotelCampaign, min_stars: parseInt(e.target.value) || 0 })}
                data-testid="hotel-campaign-stars"
              />
              <Input
                placeholder="Max/giorno"
                type="number"
                min="1"
                max="95"
                value={hotelCampaign.max_batch_per_day}
                onChange={(e) => setHotelCampaign({ ...hotelCampaign, max_batch_per_day: parseInt(e.target.value) || 90 })}
                data-testid="hotel-campaign-max"
              />
            </div>
            <div className="flex gap-2">
              <Button onClick={createHotelCampaign} className="rounded-full bg-violet-600 hover:bg-violet-700" data-testid="launch-hotel-campaign-btn">
                <Sparkles className="w-4 h-4 mr-1" /> Crea Campagna
              </Button>
              <Button variant="outline" className="rounded-full" onClick={() => setShowHotelCampaign(false)}>
                Annulla
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Manual Queue Form */}
      {showCreate && (
        <Card className="rounded-xl border-violet-200 bg-violet-50/30">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-medium">Crea Nuova Coda Email (Manuale)</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Input
              placeholder="Nome campagna *"
              value={newQueue.campaign_name}
              onChange={(e) => setNewQueue({ ...newQueue, campaign_name: e.target.value })}
              data-testid="queue-campaign-name"
            />
            <Input
              placeholder="Oggetto email *"
              value={newQueue.subject}
              onChange={(e) => setNewQueue({ ...newQueue, subject: e.target.value })}
              data-testid="queue-subject"
            />
            <Textarea
              placeholder="Incolla qui le email (separate da spazio, virgola o a capo) *"
              value={newQueue.emails_text}
              onChange={(e) => setNewQueue({ ...newQueue, emails_text: e.target.value })}
              rows={5}
              data-testid="queue-emails"
            />
            <Textarea
              placeholder="HTML content (opzionale - se vuoto usa testo semplice)"
              value={newQueue.html_content}
              onChange={(e) => setNewQueue({ ...newQueue, html_content: e.target.value })}
              rows={3}
            />
            <div className="flex gap-2">
              <Button onClick={createQueue} className="rounded-full bg-violet-600 hover:bg-violet-700" data-testid="save-queue-btn">
                <Check className="w-4 h-4 mr-1" /> Crea Coda
              </Button>
              <Button variant="outline" className="rounded-full" onClick={() => setShowCreate(false)}>
                Annulla
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-violet-500" />
        </div>
      ) : queues.length === 0 ? (
        <Card className="rounded-2xl border-border/50">
          <CardContent className="py-12 text-center">
            <Mail className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
            <p className="text-muted-foreground">Nessuna coda email. Crea una nuova campagna.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {queues.map((q) => (
            <Card key={q.queue_group} className="rounded-xl border-border/50" data-testid={`queue-${q.queue_group}`}>
              <CardContent className="p-4">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                  <div>
                    <h3 className="font-semibold text-sm">{q.campaign_name}</h3>
                    <p className="text-xs text-muted-foreground">{q.subject}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {new Date(q.created_at).toLocaleString("it-IT")}
                      {q.target_countries?.length > 0 && <span className="ml-2 text-violet-600">{q.target_countries.join(", ")}</span>}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-xs space-y-0.5 text-right">
                      <p className="text-green-600 font-medium">Inviate: {q.sent || 0}</p>
                      <p className="text-yellow-600">In attesa: {q.pending || 0}</p>
                      <p className="text-red-600">Fallite: {q.failed || 0}</p>
                    </div>
                    {(q.pending || 0) > 0 && (
                      <Button
                        size="sm"
                        className="rounded-full bg-green-600 hover:bg-green-700"
                        onClick={() => sendBatch(q.queue_group)}
                        disabled={sending[q.queue_group] || (sendStatus && sendStatus.remaining_today === 0)}
                        data-testid={`send-batch-${q.queue_group}`}
                      >
                        {sending[q.queue_group] ? (
                          <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                        ) : (
                          <Send className="w-4 h-4 mr-1" />
                        )}
                        Invia Batch ({Math.min(90, sendStatus?.remaining_today || 90)})
                      </Button>
                    )}
                    {(q.pending || 0) === 0 && (
                      <Badge variant="outline" className="border-green-300 text-green-700 text-xs">
                        <Check className="w-3 h-3 mr-1" /> Completata
                      </Badge>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      className="rounded-full text-red-500 hover:text-red-700 hover:bg-red-50"
                      onClick={() => deleteQueue(q.queue_group)}
                      data-testid={`delete-queue-${q.queue_group}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {/* Progress bar */}
                {q.total_emails > 0 && (
                  <div className="mt-3">
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-green-500 rounded-full h-2 transition-all"
                        style={{ width: `${((q.sent || 0) / q.total_emails) * 100}%` }}
                      />
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {q.sent || 0}/{q.total_emails} ({Math.round(((q.sent || 0) / q.total_emails) * 100)}%)
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

// ==================== TAB: PIPELINE CRM ====================
const PipelineTab = () => {
  const [hotels, setHotels] = useState({});
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState({});

  const loadPipeline = useCallback(async () => {
    setLoading(true);
    try {
      const [summaryRes, ...statusResults] = await Promise.all([
        axios.get(`${API}/hotel-spa/pipeline/summary`),
        ...PIPELINE_STATUSES.map((s) =>
          axios.get(`${API}/hotel-spa/hotels`, { params: { pipeline_status: s.value, limit: 20 } })
        ),
      ]);

      setSummary(summaryRes.data);

      const grouped = {};
      PIPELINE_STATUSES.forEach((s, i) => {
        grouped[s.value] = statusResults[i].data.hotels;
      });
      setHotels(grouped);
    } catch (e) {
      toast.error("Errore caricamento pipeline");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPipeline();
  }, [loadPipeline]);

  const updateStatus = async (hotelId, newStatus) => {
    try {
      await axios.put(`${API}/hotel-spa/pipeline/${hotelId}`, {
        hotel_id: hotelId,
        status: newStatus,
      });
      toast.success("Stato aggiornato");
      loadPipeline();
    } catch (e) {
      toast.error("Errore");
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-violet-500" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {PIPELINE_STATUSES.map((s) => (
          <Card key={s.value} className="rounded-xl border-border/50">
            <CardContent className="p-3 text-center">
              <div className={`w-3 h-3 rounded-full ${s.color} mx-auto mb-1`} />
              <p className="text-2xl font-bold">{summary[s.value] || 0}</p>
              <p className="text-xs text-muted-foreground">{s.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Pipeline Columns */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {PIPELINE_STATUSES.map((status) => (
          <Card key={status.value} className="rounded-xl border-border/50">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${status.color}`} />
                {status.label}
                <Badge variant="outline" className="text-xs ml-auto">
                  {(hotels[status.value] || []).length}
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 max-h-[400px] overflow-y-auto">
              {(hotels[status.value] || []).length === 0 ? (
                <p className="text-xs text-muted-foreground text-center py-4">Vuoto</p>
              ) : (
                (hotels[status.value] || []).map((hotel) => (
                  <div
                    key={hotel.hotel_id}
                    className="p-2 bg-secondary/30 rounded-lg text-xs space-y-1"
                  >
                    <p className="font-medium">{hotel.name}</p>
                    <p className="text-muted-foreground">
                      {hotel.city}{hotel.city && hotel.country ? ", " : ""}{hotel.country}
                    </p>
                    {hotel.contact_email && (
                      <p className="text-blue-600 truncate">{hotel.contact_email}</p>
                    )}
                    <select
                      className="w-full border rounded px-1 py-0.5 text-xs bg-background mt-1"
                      value={hotel.pipeline_status}
                      onChange={(e) => updateStatus(hotel.hotel_id, e.target.value)}
                    >
                      {PIPELINE_STATUSES.map((s) => (
                        <option key={s.value} value={s.value}>{s.label}</option>
                      ))}
                    </select>
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

// ==================== TAB: STATISTICHE ====================
const StatsTab = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/hotel-spa/stats`);
        setStats(res.data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-violet-500" />
      </div>
    );
  }

  if (!stats) return null;

  return (
    <div className="space-y-6">
      {/* Main Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="rounded-xl border-border/50 bg-gradient-to-br from-violet-50 to-purple-50">
          <CardContent className="p-4 text-center">
            <Building2 className="w-6 h-6 mx-auto text-violet-600 mb-2" />
            <p className="text-3xl font-bold text-violet-600">{stats.total_hotels}</p>
            <p className="text-xs text-muted-foreground">Hotel Totali</p>
          </CardContent>
        </Card>
        <Card className="rounded-xl border-border/50 bg-gradient-to-br from-teal-50 to-cyan-50">
          <CardContent className="p-4 text-center">
            <Sparkles className="w-6 h-6 mx-auto text-teal-600 mb-2" />
            <p className="text-3xl font-bold text-teal-600">{stats.with_spa}</p>
            <p className="text-xs text-muted-foreground">Con SPA</p>
          </CardContent>
        </Card>
        <Card className="rounded-xl border-border/50 bg-gradient-to-br from-blue-50 to-indigo-50">
          <CardContent className="p-4 text-center">
            <Mail className="w-6 h-6 mx-auto text-blue-600 mb-2" />
            <p className="text-3xl font-bold text-blue-600">{stats.with_email}</p>
            <p className="text-xs text-muted-foreground">Con Email</p>
          </CardContent>
        </Card>
        <Card className="rounded-xl border-border/50 bg-gradient-to-br from-green-50 to-emerald-50">
          <CardContent className="p-4 text-center">
            <Send className="w-6 h-6 mx-auto text-green-600 mb-2" />
            <p className="text-3xl font-bold text-green-600">{stats.total_emails_sent}</p>
            <p className="text-xs text-muted-foreground">Email Inviate</p>
          </CardContent>
        </Card>
      </div>

      {/* By Country */}
      {Object.keys(stats.by_country || {}).length > 0 && (
        <Card className="rounded-xl border-border/50">
          <CardHeader>
            <CardTitle className="font-serif text-lg flex items-center gap-2">
              <Globe className="w-5 h-5 text-violet-500" />
              Hotel per Paese
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {Object.entries(stats.by_country).map(([country, count]) => (
                <Badge key={country} variant="outline" className="px-3 py-1 text-sm">
                  {country}: {count}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Pipeline */}
      {Object.keys(stats.pipeline || {}).length > 0 && (
        <Card className="rounded-xl border-border/50">
          <CardHeader>
            <CardTitle className="font-serif text-lg flex items-center gap-2">
              <Users className="w-5 h-5 text-violet-500" />
              Pipeline CRM
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              {PIPELINE_STATUSES.map((s) => (
                <div key={s.value} className="text-center p-3 bg-secondary/30 rounded-lg">
                  <div className={`w-2 h-2 rounded-full ${s.color} mx-auto mb-1`} />
                  <p className="text-xl font-bold">{stats.pipeline[s.value] || 0}</p>
                  <p className="text-xs text-muted-foreground">{s.label}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* By Priority */}
      {Object.keys(stats.by_priority || {}).length > 0 && (
        <Card className="rounded-xl border-border/50">
          <CardHeader>
            <CardTitle className="font-serif text-lg flex items-center gap-2">
              <Star className="w-5 h-5 text-yellow-500" />
              Hotel per Priorita
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-4">
              {Object.entries(stats.by_priority).map(([priority, count]) => (
                <div key={priority} className={`px-4 py-2 rounded-lg border ${PRIORITY_COLORS[priority] || ""}`}>
                  <p className="text-lg font-bold">{count}</p>
                  <p className="text-xs capitalize">{priority}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// ==================== MAIN PAGE ====================
const HotelSpaPartnerPage = () => {
  const [activeTab, setActiveTab] = useState("search");

  return (
    <PageLayout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12" data-testid="hotel-spa-page">
        <div className="mb-8">
          <h1 className="font-serif text-3xl md:text-4xl font-semibold text-foreground mb-2">
            Partner Hotel / Luxury SPA
          </h1>
          <p className="text-muted-foreground">
            Sistema integrato per ricerca hotel, CRM e campagne email ODR
          </p>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-6 border-b pb-4" data-testid="hotel-tabs">
          {TABS.map((tab) => (
            <Button
              key={tab.id}
              variant={activeTab === tab.id ? "default" : "outline"}
              size="sm"
              className={`rounded-full ${
                activeTab === tab.id
                  ? "bg-violet-600 hover:bg-violet-700 text-white"
                  : ""
              }`}
              onClick={() => setActiveTab(tab.id)}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="w-4 h-4 mr-1" />
              {tab.label}
            </Button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === "search" && <SearchTab />}
        {activeTab === "generic" && <GenericSearchTab />}
        {activeTab === "hotels" && <HotelsTab />}
        {activeTab === "campaigns" && <CampaignsTab />}
        {activeTab === "pipeline" && <PipelineTab />}
        {activeTab === "stats" && <StatsTab />}
      </div>
    </PageLayout>
  );
};

export default HotelSpaPartnerPage;
